import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bradford\'s AMBER DfT rating strengthen my claim?',
    answer: 'It can, but no rating guarantees success. The Department for Transport rates Bradford AMBER overall and on condition, with GREEN on spend and best practice. That AMBER condition scorecard reflects network-level concerns — including 76% of unclassified roads in red or amber condition at the 2024 survey. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired.',
  },
  {
    question: 'Bradford says the pothole was below their intervention criteria. Does that matter?',
    answer: 'Not necessarily if it damaged your vehicle. Bradford\'s transparency report states there is no minimum pothole size — investigatory dimensions are 300mm by 40mm on carriageways — but potholes below those dimensions will still be filled where there is a high probability of risk. A defect that damaged your vehicle clearly posed a risk. Intervention dimensions do not override the duty to maintain roads reasonably under Section 41 of the Highways Act 1980.',
  },
  {
    question: 'What if my pothole was on an unclassified residential road?',
    answer: 'Unclassified roads make up 1,474.29km — 79% of Bradford\'s 1,855km carriageway network. At the 2024 condition survey, 14.48% of U-roads were in red category and 61.61% in amber — roughly 213km needing maintenance now and 908km that may need it soon. Safety inspections on U-roads are annual. If your incident fell between surveys, network-level percentages may not capture the defect on your street.',
  },
  {
    question: 'Why are pothole repairs up 83% while preventative spending is rising?',
    answer: 'Bradford filled 6,411 potholes in 2020/21 and 11,761 in 2024/25 — an 83% rise. Over the same period, estimated preventative maintenance share climbed from 66% to a projected 73%, and DfT capital allocation rose from £8.05m to £13.20m. The council\'s own report notes amber-condition U-roads have increased over five years and estimates around 11,000 pothole fills for 2025/26 despite preventative work — evidence that reactive workload remains high.',
  },
  {
    question: 'Bradford has GREEN spend and best-practice scorecards — can I still claim?',
    answer: 'Yes. GREEN spend reflects projected 2025/26 capital spend of £13.20m matching the full DfT allocation via the West Yorkshire Combined Authority CRSTS settlement, with 73% classed as preventative. GREEN best practice reflects Bradford\'s published plans on innovation, decarbonisation and footway maintenance. Neither scorecard proves the individual pothole was known and repaired within inspection intervals on your road.',
  },
  {
    question: 'What if the pothole has been repaired since my incident?',
    answer: 'You can still claim. A subsequent repair can evidence that the council acknowledged a defect existed. Bradford filled 11,761 potholes in 2024/25 — reactive patching that does not automatically defeat a claim if the defect existed long enough to have been found by routine inspection or prior report before your damage.',
  },
  {
    question: 'How do I report a pothole to Bradford Council?',
    answer: 'Report potholes and uneven surfaces via Bradford\'s My Requests portal at bradford.gov.uk. You will need the street name, surface type and pothole size. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BradfordCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bradford Pothole Claims | 11,761 Repairs on Amber-Rated Roads | Fixtyer"
        description="Bradford Council earns AMBER DfT condition ratings with 11,761 pothole repairs in 2024/25 — up 83% since 2020/21 — while 76% of residential roads need maintenance. See what this means for your Section 58 claim."
        canonical="/councils/bradford"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bradford', url: '/councils/bradford' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bradford Council"
        councilSlug="bradford"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bradford"
        headline="Bradford Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bradford</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            11,761 Pothole Repairs in 2024/25 on an Amber-Rated Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bradford earns <strong className="text-amber-400">AMBER</strong> DfT scorecards for overall performance
            and road condition, with <strong className="text-emerald-400">GREEN</strong> on spend and best practice.
            Its own transparency report records <strong className="text-rose-400">11,761 pothole fills in 2024/25</strong>,
            up 83% from 2020/21, while <strong className="text-amber-400">76% of unclassified roads</strong> sit in
            red or amber condition. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">82</div>
            <div className="text-white font-medium mt-2">Years per U-road resurfacing cycle</div>
            <div className="text-brand-300 text-sm mt-1">
              Bradford plans 18km of unclassified road resurfacing in 2025/26 against 1,474km of residential network —
              1.2% annually — while 213km of U-roads are already in red condition.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,855km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Bradford's June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,855km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">1,474km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">79% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">381km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A, B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">183km A · 78km B · 120km C</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">3,042km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footways</div>
              <div className="text-xs text-surface-500 mt-1">Plus 1,074km public rights of way</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Funding caveat:</strong> DfT capital funding for Bradford is administered by the West Yorkshire
              Combined Authority as part of the City Region Sustainable Transport Settlement (CRSTS), a five-year
              settlement from 2022/23 to 2026/27. The £13.20m 2025/26 allocation funds planned preventative work;
              reactive pothole filling is funded from separate council revenue budgets (£4.77m projected for 2025/26).
            </p>
          </div>

          <QuoteCard
            quote="Our unclassified roads make up 79% of our network and we will continue to prioritise the reduction of percentages in the Red and Amber categories over the near term and increase those in the Green category."
            source="Bradford Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            HD camera surveys each spring or summer — red, amber and green grades reported to the DfT annually
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Survey caveat:</strong> Bradford employs an external asset management company to survey all
              classified and unclassified roads annually. Condition grades reflect damage types including potholes,
              cracking, rutting and chipping loss at the time of survey — not necessarily the condition on the day
              of your incident. From 2026/27, PAS 2161 will replace the current three-category system with five
              condition bands nationally.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (183.1km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7.23%</td>
                    <td className="px-4 py-2 text-center">59.16%</td>
                    <td className="px-4 py-2 text-center">33.61%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6.87%</td>
                    <td className="px-4 py-2 text-center">64.03%</td>
                    <td className="px-4 py-2 text-center">28.73%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">6.17%</td>
                    <td className="px-4 py-2 text-center font-bold">62.61%</td>
                    <td className="px-4 py-2 text-center font-bold">31.22%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road red condition has fallen since 2021, but amber-category A-roads remain above 62% — principal
              routes where maintenance may be required soon still dominate the class.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (197.6km)</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">11.08%</td>
                    <td className="px-4 py-2 text-center">60.84%</td>
                    <td className="px-4 py-2 text-center">28.08%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">9.15%</td>
                    <td className="px-4 py-2 text-center">63.90%</td>
                    <td className="px-4 py-2 text-center">26.76%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">8.03%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">65.40%</td>
                    <td className="px-4 py-2 text-center font-bold">26.57%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C red condition improved from 11.08% in 2020 to 8.03% in 2024, but amber share worsened again in
              2024 and remains higher than 2020 — fewer green-rated B/C roads than five years ago.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (1,474km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">18.79%</td>
                    <td className="px-4 py-2 text-center">57.32%</td>
                    <td className="px-4 py-2 text-center">23.89%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">16.60%</td>
                    <td className="px-4 py-2 text-center">57.95%</td>
                    <td className="px-4 py-2 text-center">22.22%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">14.48%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">61.61%</td>
                    <td className="px-4 py-2 text-center font-bold">23.91%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Red U-road share fell from 18.79% to 14.48% over five years, but amber U-roads rose from 57.32% to
              61.61% — roughly 908km where maintenance may be required soon, on roads where safety inspections
              are annual and pothole damage claims are most common.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but reactive maintenance still accounts for over a quarter of highways budgets
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bradford highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">13,204,870</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">13,204,870</td>
                  <td className="px-4 py-3 text-center">4,770,000</td>
                  <td className="px-4 py-3 text-center text-emerald-400">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">11,464,965</td>
                  <td className="px-4 py-3 text-center">15,288,291</td>
                  <td className="px-4 py-3 text-center">3,974,648</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">13,888,071</td>
                  <td className="px-4 py-3 text-center">12,296,573</td>
                  <td className="px-4 py-3 text-center">4,469,519</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">10,515,200</td>
                  <td className="px-4 py-3 text-center">8,851,841</td>
                  <td className="px-4 py-3 text-center">4,866,173</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center">35%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">8,055,256</td>
                  <td className="px-4 py-3 text-center">9,303,652</td>
                  <td className="px-4 py-3 text-center">4,825,913</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">10,228,187</td>
                  <td className="px-4 py-3 text-center">8,548,992</td>
                  <td className="px-4 py-3 text-center">4,414,734</td>
                  <td className="px-4 py-3 text-center">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend matches the full £13.20m DfT allocation — up 64% from the £8.05m
                allocation in 2021/22 — with preventative maintenance share rising from 66% to a projected 73%.
                The council also plans 75km of surface dressing and £3.34m of micro asphalt and surface dressing
                work in 2025/26.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Reactive works budget is £4.77m for 2025/26 — pothole filling, emergency repairs and gritting.
                Pothole repairs cost £194,756 in 2024/25, equating to 9.5% of the reactive roads and footways
                budget. Aggregate capital investment does not prove every defect was caught within annual inspection
                intervals on your street.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Total potholes filled — following reports to the contact centre and identification by highway inspectors
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Bradford potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">6,411</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">8,490</td>
                  <td className="px-6 py-4 text-center text-amber-600">+32.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">10,285</td>
                  <td className="px-6 py-4 text-center text-amber-600">+60.4%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13,289</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+107.3%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">11,761</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">+83.4%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (estimated)</td>
                  <td className="px-6 py-4 text-center font-bold">~11,000</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council rough estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <QuoteCard
              quote="It is impossible to estimate the number of potholes we are likely to fill within the financial year but a rough estimate would be around 11,000. Whilst preventative works are helping to reduce the overall numbers of potholes on our network, heavy rainfall events this summer along with a cold and/or wet winter, will potentially cause potholes to form on those roads that have not received preventative work."
              source="Bradford Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Over 32 patches a day</h3>
              <p className="text-surface-600 text-sm">
                11,761 pothole repairs in 2024/25 works out to roughly 32 patches per day across 1,855km. Peak
                volume hit 13,289 in 2023/24. The council uses a risk-based approach aligned to Well-Managed
                Highway Infrastructure — no fixed minimum pothole size, with investigatory dimensions of 300mm by
                40mm on carriageways.
              </p>
            </div>
          </div>

          {/* Resurfacing maths */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-semibold text-amber-900 mb-3">The 82-year U-road cycle</h3>
            <p className="text-amber-800 text-sm mb-3">
              Bradford plans 18km of unclassified road resurfacing in 2025/26 from a £3.6m allocation — 1.2% of
              the 1,474km U-road network. At that rate, each residential road is resurfaced roughly once every
              82 years, while 213km of U-roads are already in red condition requiring maintenance now.
            </p>
            <p className="text-amber-800 text-sm">
              Clearing the current red backlog alone would take nearly 12 years at 18km per year — assuming no
              additional roads deteriorate during that period.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Bradford says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>Condition surveys:</strong> annual HD camera surveys on all road classes each spring or summer</li>
                <li>• <strong>City and town centres:</strong> monthly safety inspections</li>
                <li>• <strong>B roads and bus routes:</strong> quarterly safety inspections</li>
                <li>• <strong>Unclassified roads:</strong> annual safety inspections</li>
                <li>• <strong>Gullies:</strong> cyclic cleansing regime across 98,864 gullies</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Pothole intervention</h3>
              <p className="text-surface-600 text-sm mb-3">
                Bradford uses investigatory dimensions of 300mm horizontal and 40mm depth on carriageways, and
                150mm by 25mm on footways — but fills smaller defects where tripping or injury risk is high.
              </p>
              <p className="text-surface-600 text-sm">
                Pothole filling follows reports to the contact centre and identification by inspectors. Defect
                locations with prior vehicle damage or personal injury accidents are prioritised in annual
                maintenance programmes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Bradford must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              an amber DfT scorecard with green spend.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road inspected within the past 12 months?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (My Requests, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="However, if there is a high probability of risk (e.g. a pedestrian tripping/falling) and a potentially high impact on the pedestrian (e.g. significant injury,) but the pothole is less than these dimensions, it will be filled."
            source="Bradford Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Bradford Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On climate and pothole formation</h3>
              <QuoteCard
                quote="Extremes of temperatures in the summer and winter months and increasing rainfall events and storms will all have the potential to cause damage to roads, footways and bridges, and for drainage systems (including gullies) to be overwhelmed. These extremes, coupled with the traditional freezing and thawing process, will accelerate the formation of defect such as potholes."
                source="Bradford Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On reactive vs planned maintenance</h3>
              <QuoteCard
                quote="Although there will always be a requirement for reactive work, the Council continues to work hard to reduce the amount."
                source="Bradford Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On U-road priorities</h3>
              <QuoteCard
                quote="The percentage of Unclassified roads where maintenance may be required soon (Amber category) has increased over the 5-year period and is now higher than in 2020."
                source="Bradford Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an Amber-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bradford invests through CRSTS and publishes strong best-practice plans — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £13.20m capital allocation fully deployed in 2025/26 with 73% preventative
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road red condition fell from 18.79% to 14.48% between 2020 and 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual HD camera surveys on all road classes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented lifecycle planning aligned to Well-Managed Highway Infrastructure
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a prepared Section 58 defence backed by published inspection regimes. Generic neglect arguments will not land.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and condition DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,474km of U-roads — 79% of network — with 76% in red or amber at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 11,761 pothole fills in 2024/25 — up 83% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits amber U-road share is higher than in 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 18km annual U-road resurfacing against 213km red backlog — 82-year cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~11,000 pothole fills still projected for 2025/26 despite preventative work
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and annual condition surveys, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, cite the 14.48% red rate and annual inspection interval</li>
              <li>• Whether the defect persisted long enough to have been found by the council's own regime</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Bradford's
              own transparency data where it helps you — without pretending the council is failing on every measure.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Bradford Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.bradford.gov.uk/transport-and-travel/report-issues/report-a-pothole-or-uneven-surface/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — bradford.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bradford?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An amber-rated network with rising patch counts demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 83% pothole increase cited</li>
                <li>• No prior-report search</li>
                <li>• No 82-year U-road cycle argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 76% U-road red/amber condition documented</li>
                <li>✅ 11,761 pothole repairs in 2024/25 cited</li>
                <li>✅ 82-year resurfacing cycle argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bradford</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bradford"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bradford Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://bradford.gov.uk/transport-and-travel/highways-asset-management/local-highway-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bradford Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
